import React, { createContext, useMemo, useState, PropsWithChildren, useEffect, useCallback } from "react";
import { useFeatureFlag } from "@imaginelearning/shared-components/useFeatureFlag";
import useStorage from "../../hooks/useStorage";
import useUser from "../../hooks/useuser";

/*
  On Demand Tutoring - Rules

  Internal State:
  Visible is in Instruction or Guided Instrauction AND the current segment has a question
  Enabled is when the student has made at least 1 attempt at a question
  Available is when the student has made at least 1 attempt at a question
  showNudge is when a student has made 2 attempts at a question

  External State:
  As above
  showNudge is when internal show nudge and last seen is at least 1 week ago

  closeNudge used to close modal and if showNudge is active then sets the Last Seen date in local storage
*/

type OnDemandTutoringProps = {};

type OnDemandTutoringContextType = {
  visible: boolean;
  enabled: boolean;
  available: boolean;
  showNudge: boolean;
  areTeachersAvailable: boolean;
  setVisible: (newValue: boolean) => void;
  setEnabled: (newValue: boolean) => void;
  setAvailable: (newValue: boolean) => void;
  setShowNudge: (newValue: boolean) => void;
  closeNudge: () => void;
};

const OnDemandTutoringContext = createContext<OnDemandTutoringContextType>({
  visible: false,
  enabled: false,
  available: false,
  showNudge: false,
  areTeachersAvailable: true,
  setVisible: () => {},
  setEnabled: () => {},
  setAvailable: () => {},
  setShowNudge: () => {},
  closeNudge: () => {}
});

const greaterThanOneWeek = (datetime: number) => Date.now() - datetime > 604800000;

function OnDemandTutoringProvider(props: PropsWithChildren<OnDemandTutoringProps>) {
  const { children } = props;

  const [visible, setVisible] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [available, setAvailable] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [availability, setAvailability] = useState({ areTutorsAvailable: true, schedule: [], loaded: false });
  const { user } = useUser();
  const { getFlagValue } = useFeatureFlag();
  const [timezone, setTimezone] = useState<string>("");

  useEffect(() => {
    const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const overRideTimeZone = localStorage.getItem("timezone");
    if (overRideTimeZone) {
      setTimezone(overRideTimeZone);
    } else {
      setTimezone(localTimeZone);
    }
  }, []);

  useEffect(() => {
    const TWO_WEEKS_IN_MS = 15 * 24 * 60 * 60 * 1000;
    if (user && !lastSeen) {
      setLastSeen(Date.now() - TWO_WEEKS_IN_MS);
    }
  }, [user]);

  function isUSTimeZone(timeZone: string) {
    // Modify this with a list of Valid Timezones
    return timeZone.startsWith("America/") || timeZone.startsWith("US/");
  }

  const availableForTimeZone = () => isUSTimeZone(timezone);

  const fetchAvailability = () => {
    if (!timezone || timezone === "") {
      setAvailability({ areTutorsAvailable: true, schedule: [], loaded: false });
      return;
    }
    if (!isUSTimeZone(timezone)) {
      setAvailability({ areTutorsAvailable: false, schedule: [], loaded: true });
      return;
    }
    const url = new URL(window.STUDENT_ENV.REACT_APP_ONDEMAND_IDENTIFY_URL);
    const availabilityURL = `${url.protocol}//${url.host}/odt/availability?timezone=${timezone}`;
    fetch(availabilityURL)
      .then((response) => response.json())
      .then((data) => {
        setAvailability({ ...data, loaded: true });
      })
      .catch((error) => {
        setAvailability({ areTutorsAvailable: true, schedule: [], loaded: true });
      });
  };

  // create a use effect that will fetch the available hours on provider start up
  useEffect(() => {
    // fetch the available hours
    if (!window.STUDENT_ENV.REACT_APP_ONDEMAND_IDENTIFY_URL) {
      return;
    }
    if (!availability?.loaded) {
      fetchAvailability();
    }
  }, [enabled, visible, available, availability, timezone]);

  const [lastSeen, setLastSeen] = useStorage(`OnDemand-lastSeenNudge`, 0, [user?.id]);

  const featureFlag = getFlagValue("enableOnDemandTutoring") === "on";

  const closeNudge = useCallback(() => {
    // Only update the last seen date, when the Nudge window is closed by the student
    if (showNudge && availability?.areTutorsAvailable) {
      setLastSeen(Date.now());
    }
    setShowNudge(false);
  }, [setLastSeen, showNudge]);

  const providerValue = useMemo(
    () => ({
      visible: availableForTimeZone() && featureFlag && visible,
      enabled: featureFlag && enabled,
      available: featureFlag && enabled && available,
      showNudge:
        featureFlag &&
        enabled &&
        available &&
        showNudge &&
        availability?.areTutorsAvailable &&
        greaterThanOneWeek(lastSeen),
      areTeachersAvailable: availability.areTutorsAvailable,
      setVisible,
      setEnabled,
      setAvailable,
      setShowNudge,
      closeNudge
    }),
    [
      timezone,
      featureFlag,
      visible,
      enabled,
      available,
      showNudge,
      setVisible,
      setEnabled,
      setAvailable,
      setShowNudge,
      closeNudge,
      lastSeen,
      availability.areTutorsAvailable
    ]
  );

  return <OnDemandTutoringContext.Provider value={providerValue}>{children}</OnDemandTutoringContext.Provider>;
}

export { OnDemandTutoringContext, OnDemandTutoringProvider };
export type { OnDemandTutoringContextType };
